import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ChevronRight, Megaphone, Plus, ShieldAlert } from 'lucide-react';
import { extend, getNotices } from '@/lib/api';
import { formatAge, formatRelativeDay, isoInHours } from '@/lib/format';
import type { NoticeRow, Session } from '@/lib/types';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Empty, Loading } from '@/components/shell';
import {
  AnalyticsBlock,
  AnalyticsGrid,
  BigNumber,
  Disponibili,
  Progression,
} from '@/components/analytics';

/**
 * La home: l'azione, i numeri, quello che è in corso.
 *
 * Prima i tre blocchi, nell'ordine delle domande che ci si fa aprendo la
 * console — quanti mi seguono, quanto ho pubblicato, quanto posso ancora
 * notificare — e poi il banner che pubblica. In quest'ordine perché l'ultimo
 * dei tre numeri è il vincolo che decide se quella notifica si può mandare:
 * chiederlo dopo averla scritta è il modo più rapido per farsi odiare da chi
 * usa la console sotto pressione, e chi la usa la usa sotto pressione per
 * definizione.
 *
 * Il budget resta in ALTO e non accanto al bottone «Pubblica». Far fallire una
 * form dopo che l'hanno compilata è il modo più rapido per farsi odiare da chi
 * la usa sotto pressione — e chi usa questa console la usa sotto pressione per
 * definizione.
 */
export function Stato({ session }: { session: Session }) {
  const notices = useQuery({
    queryKey: ['notices', session.istat],
    queryFn: () => getNotices(session.istat, 50),
    refetchInterval: 60_000,
  });

  const attivi = (notices.data ?? []).filter((n) => n.stato === 'attivo');
  const canPublish = session.is_active && session.console_enabled;
  const { quota, activity } = session;
  const dailyOut = quota.daily_used >= quota.daily_cap;
  const weeklyOut = quota.weekly_used >= quota.weekly_cap;
  const restantiOggi = Math.max(0, quota.daily_cap - quota.daily_used);
  const restantiSettimana = Math.max(0, quota.weekly_cap - quota.weekly_used);

  return (
    <div className="space-y-7">
      <section aria-labelledby="analytics">
        <h2 id="analytics" className="sr-only">
          I numeri del canale
        </h2>

        <AnalyticsGrid>
          {/* 1 — Chi segue. Un numero solo, e non per pigrizia: il seguito di
                 un singolo Comune non ha una serie storica da nessuna parte
                 (`analytics_daily` fotografa l'Italia), e una curva disegnata
                 su un punto solo sarebbe inventata. */}
          <AnalyticsBlock label="Chi segue il Comune">
            <BigNumber value={session.recipients} unit="dispositivi" />
          </AnalyticsBlock>

          {/* 2 — Cosa ho pubblicato. Le stesse finestre del budget qui sotto,
                 o due numeri accanto parlerebbero di periodi diversi. */}
          <AnalyticsBlock label="Avvisi pubblicati">
            <Progression
              righe={[
                { label: 'Oggi', value: activity?.today ?? null },
                { label: 'Ultimi 7 giorni', value: activity?.week ?? null },
                { label: 'Ultimi 30 giorni', value: activity?.month ?? null },
              ]}
            />
          </AnalyticsBlock>

          {/* 3 — Quanto posso ancora notificare. Rosso solo quando è finito,
                 non quando «sta per»: un allarme che arriva presto è un
                 allarme che si impara a ignorare. */}
          <AnalyticsBlock
            label="Notifiche disponibili"
            tone={dailyOut || weeklyOut ? 'danger' : 'default'}
          >
            <Disponibili
              righe={[
                {
                  label: 'Oggi',
                  restanti: restantiOggi,
                  cap: quota.daily_cap,
                },
                {
                  label: 'Questa settimana',
                  restanti: restantiSettimana,
                  cap: quota.weekly_cap,
                },
              ]}
            />
          </AnalyticsBlock>
        </AnalyticsGrid>

        {(quota.alert_boost || dailyOut || weeklyOut) && (
          <div className="mt-4 space-y-3">
            {/* Il boost è anche il modo in cui l'operatore scopre che il
                sistema sa cosa sta succedendo sul suo territorio. */}
            {quota.alert_boost && (
              <Alert className="border-gialla bg-gialla/25">
                <ShieldAlert />
                <AlertTitle>Allerta ufficiale in corso sul Comune</AlertTitle>
                <AlertDescription>
                  Il limite di oggi è alzato a {quota.daily_cap} push.
                </AlertDescription>
              </Alert>
            )}
            {/* La riga che il riquadro non dice più da sé, ora che le note
                sono uscite: un tetto pieno non impedisce di pubblicare. È la
                valvola che rende accettabili i tetti, e va detta quando
                scattano — non tutti i giorni sotto un numero che sta bene. */}
            {(dailyOut || weeklyOut) && (
              <Alert variant="destructive">
                <AlertTriangle />
                <AlertTitle>
                  Push {dailyOut && weeklyOut
                    ? 'esaurite'
                    : dailyOut
                      ? 'di oggi esaurite'
                      : 'della settimana esaurite'}
                </AlertTitle>
                <AlertDescription>
                  {weeklyOut && !dailyOut
                    ? `Il tetto dei sette giorni è pieno: oggi ne restano ${restantiOggi} sul limite giornaliero, ma non partiranno. `
                    : ''}
                  Puoi ancora pubblicare avvisi: restano visibili in app senza
                  notificare nessuno.
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}

        {/* Qui stava l'avviso «Ore di silenzio — notificano solo le
            emergenze». Non c'è più né la fascia (tolta dalla validazione il
            13/08/2026) né l'emergenza (tolta dalla console il 14/08/2026):
            restava una regola dichiarata che il server non applicava. */}

        {/* Il banner DOPO i numeri, non prima: si guarda quanto budget resta e
            poi si decide se premere. Prima stava in cima, e chiedeva di
            scegliere prima di aver letto il vincolo che decide la scelta. */}
        {canPublish && (
          <div className="mt-5">
            <BannerPubblica />
          </div>
        )}
      </section>

      <section>
        <h2 className="mb-3 text-[19px] font-bold text-ink">
          {attivi.length === 0
            ? 'Avvisi in corso'
            : `${attivi.length} avvis${attivi.length === 1 ? 'o' : 'i'} in corso`}
        </h2>

        {notices.isLoading && <Loading what="Carico gli avvisi" />}

        {notices.isError && (
          <Alert variant="destructive">
            <AlertTriangle />
            <AlertTitle>Non riesco a leggere gli avvisi</AlertTitle>
            <AlertDescription>
              Ricarica la pagina. Gli avvisi già pubblicati restano comunque
              visibili in app: il guasto è qui, non lì.
            </AlertDescription>
          </Alert>
        )}

        {notices.isSuccess && attivi.length === 0 && (
          <Empty>
            Nessun avviso in corso. Chi segue il Comune non sta vedendo niente.
          </Empty>
        )}

        <div className="space-y-4">
          {attivi.map((n) => (
            <ActiveCard key={n.id} notice={n} session={session} />
          ))}
        </div>
      </section>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

/**
 * Il banner di pubblicazione.
 *
 * Pieno e a tutta larghezza, sotto i numeri: è l'unica azione che questa
 * console esiste per far compiere, e durante un evento non deve essere
 * cercata. Prima era un bottone nell'angolo dell'intestazione del budget, cioè
 * la posizione che si dà a un'azione secondaria.
 *
 * Porta con sé la riga che vale più del bottone: **pubblicare non è
 * notificare**. È la valvola che rende accettabili i tetti, e va detta prima
 * di entrare nella form — non quando la quota ha già rifiutato la push.
 */
function BannerPubblica() {
  return (
    <Link
      to="/nuovo"
      className="group flex items-center gap-4 rounded-xl bg-primary px-5 py-4 text-primary-foreground transition-opacity hover:opacity-95"
    >
      <span className="flex size-11 shrink-0 items-center justify-center rounded-full bg-white/15">
        <Plus className="size-6" aria-hidden="true" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[18px] font-semibold leading-tight">
          Nuovo avviso
        </span>
        <span className="mt-0.5 block text-[14px] leading-snug text-white/75">
          Pubblicare non è notificare: puoi pubblicare senza svegliare nessuno.
        </span>
      </span>
      <ChevronRight
        className="size-5 shrink-0 text-white/60 transition-transform group-hover:translate-x-0.5"
        aria-hidden="true"
      />
    </Link>
  );
}

/* -------------------------------------------------------------------------- */

/**
 * Un avviso in corso, col sollecito quando è vecchio.
 *
 * Il sollecito è il pezzo di interfaccia che vale più di metà delle altre
 * schermate: un «acqua non potabile» rimasto su per una settimana fa più danno
 * del canale intero, e nessuno se ne accorge da solo. La proroga sta accanto
 * alla revoca proprio perché la risposta giusta, spesso, è «sì, è ancora vero».
 */
function ActiveCard({ notice, session }: { notice: NoticeRow; session: Session }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const proroga = useMutation({
    mutationFn: () => extend(session.istat, notice.id, isoInHours(24)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notices', session.istat] });
    },
  });

  return (
    <Card className={notice.needs_review ? 'border-arancione/60' : undefined}>
      <CardHeader>
        <CardTitle className="text-[18px] leading-snug">
          <Link to={`/avviso/${notice.id}`} className="hover:underline">
            {notice.title}
          </Link>
        </CardTitle>
        <CardDescription className="tnum">
          Scade {formatRelativeDay(notice.expires_at)}
          {notice.push_requested ? ' · notificato' : ' · senza notifica'}
        </CardDescription>
      </CardHeader>

      <CardContent>
        <p className="line-clamp-2 text-[15px] leading-relaxed text-ink2">
          {notice.body}
        </p>
      </CardContent>

      {notice.needs_review && (
        <CardFooter className="flex-col items-stretch gap-3">
          <Alert className="border-arancione/50 bg-arancione/10">
            <Megaphone className="text-arancione" />
            <AlertTitle>
              Attivo {formatAge(notice.published_at)}. È ancora vero?
            </AlertTitle>
            <AlertDescription>
              Un avviso che resta su quando non serve più insegna ai cittadini a
              non fidarsi del prossimo.
            </AlertDescription>
          </Alert>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => navigate(`/nuovo?revoca=${notice.id}`)}>
              Comunica il cessato
            </Button>
            <Button
              variant="outline"
              disabled={proroga.isPending}
              onClick={() => proroga.mutate()}
            >
              {proroga.isPending ? 'Proroga…' : 'Sì, proroga di 24 ore'}
            </Button>
          </div>
        </CardFooter>
      )}
    </Card>
  );
}
